import {
  computeLedgerState,
  envelopeBalance,
  type LedgerState,
  type PeriodOpening,
} from './balances'
import { isActive, type Envelope, type EnvelopeId } from './envelope'
import type {
  AdjustmentEntry,
  LedgerEntry,
  OccurrenceId,
  OperationId,
  TransferOrigin,
} from './ledger'
import { reversedOperations } from './ledger'
import { isValidAmount, sum, type Xof } from './money'
import type { PeriodId } from './period'
import { err, ok, type Result } from './result'
import {
  transferRefusal,
  transferRequiresVerification,
  type TransferRefusal,
} from './transfer-policy'

/**
 * Commandes métier. Chacune valide les règles sur l'état courant de la période et renvoie
 * les lignes à ajouter au journal, ou un refus explicite. Rien n'est écrit ici : la même
 * validation est rejouée par la base (fonctions SQL) avant toute écriture.
 */

export interface Ledger {
  periodId: PeriodId
  opening: PeriodOpening
  entries: readonly LedgerEntry[]
  envelopes: readonly Envelope[]
  /** Faits des périodes antérieures utiles à la période courante. */
  history?: LedgerHistory
}

/**
 * Ce que la période courante doit savoir des périodes précédentes :
 * - les échéances déjà réglées (une facture en retard ne se paie pas deux fois) ;
 * - les réservations encore ouvertes, déjà comprises dans `opening.envelopes[…].reserved`.
 */
export interface LedgerHistory {
  settledOccurrences: ReadonlySet<OccurrenceId>
  openReservations: Readonly<Record<OccurrenceId, { envelopeId: EnvelopeId; amount: Xof }>>
}

export interface OperationContext {
  operationId: OperationId
  occurredAt: string
}

export type OperationError =
  | { code: 'duplicate-operation' }
  | { code: 'invalid-amount' }
  | { code: 'empty-title' }
  | { code: 'unknown-envelope'; envelopeId: EnvelopeId }
  | { code: 'envelope-archived'; envelopeId: EnvelopeId }
  | { code: 'insufficient-funds'; envelopeId: EnvelopeId; available: Xof; shortfall: Xof }
  | { code: 'insufficient-unallocated'; unallocated: Xof; requested: Xof }
  | { code: 'transfer-refused'; reason: TransferRefusal }
  | { code: 'nothing-to-allocate' }
  | { code: 'duplicate-allocation-envelope'; envelopeId: EnvelopeId }
  | { code: 'occurrence-already-settled'; occurrenceId: OccurrenceId }
  | { code: 'unknown-operation'; operationId: OperationId }
  | { code: 'already-reversed'; operationId: OperationId }
  | { code: 'cannot-reverse-reversal'; operationId: OperationId }
  | { code: 'reversal-would-overdraw'; envelopeId: EnvelopeId | null; resulting: Xof }
  | { code: 'missing-reason' }

export type OperationResult = Result<
  { entries: LedgerEntry[]; requiresVerification: boolean },
  OperationError
>

function state(ledger: Ledger): LedgerState {
  return computeLedgerState(ledger.opening, ledger.entries)
}

function findEnvelope(ledger: Ledger, id: EnvelopeId): Result<Envelope, OperationError> {
  const envelope = ledger.envelopes.find((candidate) => candidate.id === id)
  if (envelope === undefined) return err({ code: 'unknown-envelope', envelopeId: id })
  if (!isActive(envelope)) return err({ code: 'envelope-archived', envelopeId: id })
  return ok(envelope)
}

function guardOperation(ledger: Ledger, context: OperationContext): OperationError | null {
  const exists = ledger.entries.some((entry) => entry.operationId === context.operationId)
  return exists ? { code: 'duplicate-operation' } : null
}

function base(ledger: Ledger, context: OperationContext, line: number) {
  return {
    operationId: context.operationId,
    line,
    periodId: ledger.periodId,
    occurredAt: context.occurredAt,
  }
}

function done(entries: LedgerEntry[], requiresVerification = false): OperationResult {
  return ok({ entries, requiresVerification })
}

/** Revenu reçu (spec §5.2). Il alimente « Non affecté ». */
export function recordIncome(
  ledger: Ledger,
  context: OperationContext,
  input: { amount: Xof; title: string; occurrenceId?: OccurrenceId },
): OperationResult {
  const duplicate = guardOperation(ledger, context)
  if (duplicate) return err(duplicate)
  if (!isValidAmount(input.amount)) return err({ code: 'invalid-amount' })
  if (input.title.trim() === '') return err({ code: 'empty-title' })
  return done([
    {
      ...base(ledger, context, 0),
      kind: 'income',
      amount: input.amount,
      title: input.title.trim(),
      occurrenceId: input.occurrenceId ?? null,
    },
  ])
}

/**
 * Répartition de l'argent non affecté (spec §4 étape 5, §5.2). La somme ne peut pas dépasser
 * le non affecté : on ne crée jamais de solde fictif.
 */
export function allocate(
  ledger: Ledger,
  context: OperationContext,
  lines: readonly { envelopeId: EnvelopeId; amount: Xof }[],
): OperationResult {
  const duplicate = guardOperation(ledger, context)
  if (duplicate) return err(duplicate)
  const meaningful = lines.filter((line) => line.amount !== 0)
  if (meaningful.length === 0) return err({ code: 'nothing-to-allocate' })

  const seen = new Set<EnvelopeId>()
  for (const line of meaningful) {
    if (!isValidAmount(line.amount)) return err({ code: 'invalid-amount' })
    if (seen.has(line.envelopeId)) {
      return err({ code: 'duplicate-allocation-envelope', envelopeId: line.envelopeId })
    }
    seen.add(line.envelopeId)
    const envelope = findEnvelope(ledger, line.envelopeId)
    if (!envelope.ok) return envelope
  }

  const requested = sum(meaningful.map((line) => line.amount))
  const { unallocated } = state(ledger)
  if (requested > unallocated) {
    return err({ code: 'insufficient-unallocated', unallocated, requested })
  }

  return done(
    meaningful.map((line, index) => ({
      ...base(ledger, context, index),
      kind: 'allocation' as const,
      envelopeId: line.envelopeId,
      amount: line.amount,
    })),
  )
}

/**
 * Dépense dans une seule enveloppe (spec §5.3, §5.5). Refusée si elle dépasse le disponible,
 * sauf si l'enveloppe autorise explicitement le découvert.
 */
export function recordExpense(
  ledger: Ledger,
  context: OperationContext,
  input: { envelopeId: EnvelopeId; amount: Xof; title: string },
): OperationResult {
  const duplicate = guardOperation(ledger, context)
  if (duplicate) return err(duplicate)
  if (!isValidAmount(input.amount)) return err({ code: 'invalid-amount' })
  if (input.title.trim() === '') return err({ code: 'empty-title' })
  const envelope = findEnvelope(ledger, input.envelopeId)
  if (!envelope.ok) return envelope

  const { available } = envelopeBalance(state(ledger), input.envelopeId)
  if (input.amount > available && !envelope.value.allowOverdraft) {
    return err({
      code: 'insufficient-funds',
      envelopeId: input.envelopeId,
      available,
      shortfall: input.amount - Math.max(available, 0),
    })
  }

  return done([
    {
      ...base(ledger, context, 0),
      kind: 'expense',
      envelopeId: input.envelopeId,
      amount: input.amount,
      title: input.title.trim(),
      occurrenceId: null,
    },
  ])
}

/** Transfert explicite entre deux enveloppes (spec §3.6). Ni revenu, ni dépense. */
export function transfer(
  ledger: Ledger,
  context: OperationContext,
  input: {
    sourceEnvelopeId: EnvelopeId
    destinationEnvelopeId: EnvelopeId
    amount: Xof
    origin: TransferOrigin
    note?: string
  },
): OperationResult {
  const duplicate = guardOperation(ledger, context)
  if (duplicate) return err(duplicate)
  if (!isValidAmount(input.amount)) return err({ code: 'invalid-amount' })
  const source = findEnvelope(ledger, input.sourceEnvelopeId)
  if (!source.ok) return source
  const destination = findEnvelope(ledger, input.destinationEnvelopeId)
  if (!destination.ok) return destination

  const refusal = transferRefusal(source.value, destination.value)
  if (refusal !== null) return err({ code: 'transfer-refused', reason: refusal })

  const { available } = envelopeBalance(state(ledger), source.value.id)
  if (input.amount > available) {
    return err({
      code: 'insufficient-funds',
      envelopeId: source.value.id,
      available,
      shortfall: input.amount - Math.max(available, 0),
    })
  }

  const note = input.note?.trim() ?? ''
  return done(
    [
      {
        ...base(ledger, context, 0),
        kind: 'transfer',
        sourceEnvelopeId: source.value.id,
        destinationEnvelopeId: destination.value.id,
        amount: input.amount,
        origin: input.origin,
        note: note === '' ? null : note,
      },
    ],
    transferRequiresVerification(source.value),
  )
}

/**
 * Met de côté le montant d'une échéance (spec §3.5). Le disponible baisse, pas le solde.
 * Une échéance n'est réservée qu'une fois.
 */
export function reserveForOccurrence(
  ledger: Ledger,
  context: OperationContext,
  input: { envelopeId: EnvelopeId; occurrenceId: OccurrenceId; amount: Xof },
): OperationResult {
  const duplicate = guardOperation(ledger, context)
  if (duplicate) return err(duplicate)
  if (!isValidAmount(input.amount)) return err({ code: 'invalid-amount' })
  const envelope = findEnvelope(ledger, input.envelopeId)
  if (!envelope.ok) return envelope

  const current = state(ledger)
  if (
    reservedFor(ledger, current, input.occurrenceId) > 0 ||
    isSettled(ledger, input.occurrenceId)
  ) {
    return err({ code: 'occurrence-already-settled', occurrenceId: input.occurrenceId })
  }
  const { available } = envelopeBalance(current, input.envelopeId)
  if (input.amount > available) {
    return err({
      code: 'insufficient-funds',
      envelopeId: input.envelopeId,
      available,
      shortfall: input.amount - Math.max(available, 0),
    })
  }

  return done([
    {
      ...base(ledger, context, 0),
      kind: 'reservation',
      envelopeId: input.envelopeId,
      amount: input.amount,
      occurrenceId: input.occurrenceId,
    },
  ])
}

/** Montant encore réservé pour une échéance, périodes antérieures comprises. */
function reservedFor(ledger: Ledger, current: LedgerState, occurrenceId: OccurrenceId): Xof {
  const carried = ledger.history?.openReservations[occurrenceId]?.amount ?? 0
  return carried + (current.reservedByOccurrence[occurrenceId] ?? 0)
}

/** Enveloppe qui porte la réservation encore active d'une échéance, s'il y en a une. */
function reservationEnvelope(ledger: Ledger, occurrenceId: OccurrenceId): EnvelopeId | null {
  const reversed = reversedOperations(ledger.entries)
  const reservation = ledger.entries.find(
    (entry) =>
      entry.kind === 'reservation' &&
      entry.occurrenceId === occurrenceId &&
      !reversed.has(entry.operationId),
  )
  if (reservation?.kind === 'reservation') return reservation.envelopeId
  return ledger.history?.openReservations[occurrenceId]?.envelopeId ?? null
}

function isSettled(ledger: Ledger, occurrenceId: OccurrenceId): boolean {
  if (ledger.history?.settledOccurrences.has(occurrenceId) === true) return true
  const reversed = reversedOperations(ledger.entries)
  return ledger.entries.some(
    (entry) =>
      !reversed.has(entry.operationId) &&
      ((entry.kind === 'expense' && entry.occurrenceId === occurrenceId) ||
        (entry.kind === 'income' && entry.occurrenceId === occurrenceId)),
  )
}

/**
 * Paiement d'une échéance (spec §5.4), en une seule opération atomique :
 * libération de la réservation éventuelle, puis dépense du montant réel.
 * Le montant réel peut différer du montant prévu.
 */
export function payOccurrence(
  ledger: Ledger,
  context: OperationContext,
  input: { envelopeId: EnvelopeId; occurrenceId: OccurrenceId; amount: Xof; title: string },
): OperationResult {
  const duplicate = guardOperation(ledger, context)
  if (duplicate) return err(duplicate)
  if (!isValidAmount(input.amount)) return err({ code: 'invalid-amount' })
  if (input.title.trim() === '') return err({ code: 'empty-title' })
  const envelope = findEnvelope(ledger, input.envelopeId)
  if (!envelope.ok) return envelope
  if (isSettled(ledger, input.occurrenceId)) {
    return err({ code: 'occurrence-already-settled', occurrenceId: input.occurrenceId })
  }

  const current = state(ledger)
  const reserved = Math.max(reservedFor(ledger, current, input.occurrenceId), 0)
  const reservedIn = reserved > 0 ? reservationEnvelope(ledger, input.occurrenceId) : null
  // La réservation ne libère du disponible que si elle est dans l'enveloppe débitée.
  const released = reservedIn === input.envelopeId ? reserved : 0
  const available = envelopeBalance(current, input.envelopeId).available + released
  if (input.amount > available && !envelope.value.allowOverdraft) {
    return err({
      code: 'insufficient-funds',
      envelopeId: input.envelopeId,
      available,
      shortfall: input.amount - Math.max(available, 0),
    })
  }

  const entries: LedgerEntry[] = []
  if (reserved > 0 && reservedIn !== null) {
    entries.push({
      ...base(ledger, context, entries.length),
      kind: 'release',
      envelopeId: reservedIn,
      amount: reserved,
      occurrenceId: input.occurrenceId,
    })
  }
  entries.push({
    ...base(ledger, context, entries.length),
    kind: 'expense',
    envelopeId: input.envelopeId,
    amount: input.amount,
    title: input.title.trim(),
    occurrenceId: input.occurrenceId,
  })
  return done(entries)
}

/** Libère la réservation d'une échéance ignorée ou reportée. */
export function releaseOccurrence(
  ledger: Ledger,
  context: OperationContext,
  input: { occurrenceId: OccurrenceId },
): OperationResult {
  const duplicate = guardOperation(ledger, context)
  if (duplicate) return err(duplicate)
  const reserved = reservedFor(ledger, state(ledger), input.occurrenceId)
  const envelopeId = reservationEnvelope(ledger, input.occurrenceId)
  if (reserved <= 0 || envelopeId === null) {
    return err({ code: 'occurrence-already-settled', occurrenceId: input.occurrenceId })
  }
  return done([
    {
      ...base(ledger, context, 0),
      kind: 'release',
      envelopeId,
      amount: reserved,
      occurrenceId: input.occurrenceId,
    },
  ])
}

/** Correction motivée d'un solde (solde d'ouverture, écart constaté). */
export function adjust(
  ledger: Ledger,
  context: OperationContext,
  input: Pick<AdjustmentEntry, 'envelopeId' | 'direction' | 'amount' | 'reason'>,
): OperationResult {
  const duplicate = guardOperation(ledger, context)
  if (duplicate) return err(duplicate)
  if (!isValidAmount(input.amount)) return err({ code: 'invalid-amount' })
  if (input.reason.trim() === '') return err({ code: 'missing-reason' })
  if (input.envelopeId !== null) {
    const envelope = findEnvelope(ledger, input.envelopeId)
    if (!envelope.ok) return envelope
  }
  return done([
    {
      ...base(ledger, context, 0),
      kind: 'adjustment',
      envelopeId: input.envelopeId,
      direction: input.direction,
      amount: input.amount,
      reason: input.reason.trim(),
    },
  ])
}

/** Motif des deux lignes d'un retour au non affecté. */
export const RETURN_REASON = 'Retour au non affecté'

/**
 * Rend tout ou partie du disponible d'une enveloppe à l'argent non affecté (avant de la
 * supprimer, ou pour répartir autrement). Deux lignes d'ajustement dans la même opération.
 */
export function returnToUnallocated(
  ledger: Ledger,
  context: OperationContext,
  input: { envelopeId: EnvelopeId; amount: Xof },
): OperationResult {
  const duplicate = guardOperation(ledger, context)
  if (duplicate) return err(duplicate)
  if (!isValidAmount(input.amount)) return err({ code: 'invalid-amount' })
  const envelope = findEnvelope(ledger, input.envelopeId)
  if (!envelope.ok) return envelope
  const { available } = envelopeBalance(state(ledger), input.envelopeId)
  if (input.amount > available) {
    return err({
      code: 'insufficient-funds',
      envelopeId: input.envelopeId,
      available,
      shortfall: input.amount - Math.max(available, 0),
    })
  }
  return done([
    {
      ...base(ledger, context, 0),
      kind: 'adjustment',
      envelopeId: input.envelopeId,
      direction: 'debit',
      amount: input.amount,
      reason: RETURN_REASON,
    },
    {
      ...base(ledger, context, 1),
      kind: 'adjustment',
      envelopeId: null,
      direction: 'credit',
      amount: input.amount,
      reason: RETURN_REASON,
    },
  ])
}

/**
 * Annulation d'une opération de la période par écriture compensatrice (spec §5.7).
 * Refusée si elle rendait négatif le non affecté ou une enveloppe sans découvert autorisé :
 * par exemple, annuler une affectation dont l'argent a déjà été dépensé.
 * Une opération d'une période antérieure se corrige par un ajustement motivé.
 */
export function reverseOperation(
  ledger: Ledger,
  context: OperationContext,
  input: { operationId: OperationId; reason: string },
): OperationResult {
  const duplicate = guardOperation(ledger, context)
  if (duplicate) return err(duplicate)
  if (input.reason.trim() === '') return err({ code: 'missing-reason' })

  const target = ledger.entries.filter((entry) => entry.operationId === input.operationId)
  if (target.length === 0) return err({ code: 'unknown-operation', operationId: input.operationId })
  if (target.some((entry) => entry.kind === 'reversal')) {
    return err({ code: 'cannot-reverse-reversal', operationId: input.operationId })
  }
  if (reversedOperations(ledger.entries).has(input.operationId)) {
    return err({ code: 'already-reversed', operationId: input.operationId })
  }

  const reversal: LedgerEntry = {
    ...base(ledger, context, 0),
    kind: 'reversal',
    reversedOperationId: input.operationId,
    reason: input.reason.trim(),
  }

  const before = state(ledger)
  const after = computeLedgerState(ledger.opening, [...ledger.entries, reversal])
  if (after.unallocated < 0) {
    return err({ code: 'reversal-would-overdraw', envelopeId: null, resulting: after.unallocated })
  }
  for (const envelope of ledger.envelopes) {
    const { available } = envelopeBalance(after, envelope.id)
    const previous = envelopeBalance(before, envelope.id).available
    if (available < 0 && available < previous && !envelope.allowOverdraft) {
      return err({ code: 'reversal-would-overdraw', envelopeId: envelope.id, resulting: available })
    }
  }

  return done([reversal])
}
